import { useState, useEffect } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiCheck, FiX, FiUser } from 'react-icons/fi';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { approveVerification, rejectVerification } from '../../hooks/useVerification';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { formatDate } from '../../utils/formatters';

export default function AdminVerificationDetail() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const rejectModal = useDisclosure();

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  useEffect(() => {
    if (!applicationId) return;
    const unsubscribe = onSnapshot(doc(db, 'verification_applications', applicationId), (snap) => {
      setApp(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    });
    return unsubscribe;
  }, [applicationId]);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await approveVerification(applicationId, app.userId);
      toast({
        title: 'Application approved',
        description: `${app.realFullName} is now verified.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      navigate('/admin/verifications');
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await rejectVerification(applicationId, app.userId, rejectionReason.trim());
      toast({
        title: 'Application rejected',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      navigate('/admin/verifications');
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setProcessing(false);
      rejectModal.onClose();
    }
  };

  if (loading) return <LoadingSpinner message="Loading application..." />;

  if (!app) {
    return (
      <VStack spacing={4} py={8}>
        <Heading size="md">Application not found</Heading>
        <Button as={Link} to="/admin/verifications" leftIcon={<Icon as={FiArrowLeft} />}>
          Back to Verifications
        </Button>
      </VStack>
    );
  }

  const statusColor = app.status === 'approved' ? 'green' : app.status === 'rejected' ? 'red' : 'orange';

  return (
    <VStack spacing={6} align="stretch">
      <Button
        as={Link}
        to="/admin/verifications"
        variant="ghost"
        leftIcon={<Icon as={FiArrowLeft} />}
        alignSelf="flex-start"
        size="sm"
      >
        Back to Verifications
      </Button>

      {/* Application Info */}
      <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between">
            <Heading size="md">Verification Application</Heading>
            <Badge colorScheme={statusColor} fontSize="sm" px={2} py={1} rounded="md">
              {app.status}
            </Badge>
          </HStack>

          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="gray.500">Full Name</Text>
            <Text mt={1}>{app.realFullName}</Text>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="gray.500">About</Text>
            <Text mt={1} whiteSpace="pre-wrap">{app.bio}</Text>
          </Box>

          <HStack spacing={8} flexWrap="wrap">
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.500">Contact Email</Text>
              <Text fontSize="sm">{app.contactEmail}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.500">Phone</Text>
              <Text fontSize="sm">{app.phoneNumber}</Text>
            </Box>
          </HStack>

          <HStack spacing={8} flexWrap="wrap">
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.500">Username</Text>
              <Text fontSize="sm">{app.userAnonymizedUsername}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.500">Account Email</Text>
              <Text fontSize="sm">{app.userEmail}</Text>
            </Box>
          </HStack>

          <HStack spacing={8} flexWrap="wrap">
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.500">Submitted</Text>
              <Text fontSize="sm">{formatDate(app.createdAt)}</Text>
            </Box>
            {app.reviewedAt && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="gray.500">Reviewed</Text>
                <Text fontSize="sm">{formatDate(app.reviewedAt)}</Text>
              </Box>
            )}
          </HStack>

          <Button
            as={Link}
            to={`/admin/users/${app.userId}`}
            size="sm"
            variant="outline"
            leftIcon={<Icon as={FiUser} />}
            alignSelf="flex-start"
          >
            View User Profile
          </Button>
        </VStack>
      </Box>

      {/* Rejection Reason (if rejected) */}
      {app.status === 'rejected' && app.rejectionReason && (
        <Alert status="error" rounded="md">
          <AlertIcon />
          <Box>
            <Text fontSize="sm" fontWeight="semibold">Rejection Reason</Text>
            <Text fontSize="sm">{app.rejectionReason}</Text>
          </Box>
        </Alert>
      )}

      {/* Actions */}
      {app.status === 'pending' && (
        <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
          <Heading size="sm" mb={4}>Actions</Heading>
          <HStack spacing={3}>
            <Button
              colorScheme="green"
              leftIcon={<Icon as={FiCheck} />}
              onClick={handleApprove}
              isLoading={processing}
            >
              Approve
            </Button>
            <Button
              colorScheme="red"
              variant="outline"
              leftIcon={<Icon as={FiX} />}
              onClick={rejectModal.onOpen}
            >
              Reject
            </Button>
          </HStack>
        </Box>
      )}

      {app.status === 'approved' && (
        <Alert status="success" rounded="md">
          <AlertIcon />
          This application has been approved. The user is now verified and can submit claims.
        </Alert>
      )}

      {/* Reject Modal */}
      <Modal isOpen={rejectModal.isOpen} onClose={rejectModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reject Application</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3}>
              This will reject the verification application. The user will not be able to submit claims.
            </Text>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>Reason (optional)</Text>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Explain why the application was rejected..."
              rows={3}
            />
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={rejectModal.onClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleReject} isLoading={processing}>
              Reject
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
